import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import tzLookup from "tz-lookup";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import "./BirthDataForm.css";
import { AlertCircle, X } from "lucide-react";
import ReportModal from "../ReportModal/ReportModal";
import { useAuth } from "../../contexts/AuthContext";
import FormattedDateInput from "./FormattedDateInput";

const generateTimezoneOptions = () => {
	const options = [];
	for (let offset = -12; offset <= 14; offset += 0.5) {
		const hours = Math.trunc(offset);
		const minutes = Math.abs(offset - hours) > 0 ? 30 : 0;
		const sign = offset >= 0 ? "+" : "-";
		const value = `${sign}${String(Math.abs(hours)).padStart(2, "0")}:${minutes === 0 ? "00" : "30"
			}`;
		options.push({
			value,
			label: `UTC${value}`,
		});
	}
	return options;
};

const DEFAULTS = {
	date: "",
	time: "",
	timezone: "+00:00",
	locationName: "",
};

// Libraries array must be static to avoid LoadScript reload warning
const LIBRARIES = ["places"];

const ValidationMessage = ({ children }) =>
	children ? <p className='birth-form__validation'>{children}</p> : null;

ValidationMessage.propTypes = {
	children: PropTypes.node,
};

ValidationMessage.defaultProps = {
	children: null,
};

const parseDate = (value) => {
	const [year, month, day] = value.split("-").map(Number);
	return { year, month, day };
};

const parseTime = (value) => {
	const [hour, minute] = value.split(":").map(Number);
	return { hour, minute, second: 0 };
};

const MIN_BIRTH_DATE = "1501-01-01";

export const BirthDataForm = ({ defaultValues, onSubmit, submitting, isUpdateProfile = false }) => {
	const timezoneOptions = useMemo(generateTimezoneOptions, []);
	const [selectedPlace, setSelectedPlace] = useState(null);
	const autocompleteRef = useRef(null);
	const debounceTimerRef = useRef(null);

	// const inputRef = useRef(null);
	const [isReportModalOpen, setIsReportModalOpen] = useState(false);
	const [showUpgradeModal, setShowUpgradeModal] = useState(false);
	const navigate = useNavigate();

	const handleReportPurchase = (planType, addons) => {
		console.log("Purchase:", { planType, addons });
		// TODO: Integrate with payment API
		// handlePayment(planType);
		setIsReportModalOpen(false);
	};

	const { user } = useAuth();
	const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

	const { isLoaded, loadError } = useLoadScript({
		googleMapsApiKey,
		libraries: LIBRARIES,
	});

	// Check if form should be locked (User has data AND is not premium)
	// We determine "has data" by checking if user.date exists
	const hasSavedData = !!(user?.date?.year && user?.location?.name);
	const isPremium = user?.planTier === 'premium';

	// Inputs remain enabled; when a non-premium user with saved data interacts
	// with restricted fields we show the upgrade modal instead of toggling locks.

	const checkRestriction = (fieldName) => {
		// DOB (date) is always editable.
		// Other fields are restricted if user has saved data and is not premium.
		if (fieldName === 'date') return false;

		if (hasSavedData && !isPremium) {
			setShowUpgradeModal(true);
			return true; // Restricted
		}
		return false;
	};

	const handleInteraction = (e, fieldName) => {
		if (checkRestriction(fieldName)) {
			e.preventDefault();
			e.target.blur();
		}
	};


	// Derive initial values from user object if available
	const initialValues = useMemo(() => {
		if (!hasSavedData) return { ...DEFAULTS, ...defaultValues };

		const { year, month, day } = user.date;
		const { hour, minute } = user.time;

		// Format date as YYYY-MM-DD
		const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
		const timeStrFixed = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

		return {
			date: dateStr,
			time: timeStrFixed,
			timezone: user.timezone || DEFAULTS.timezone,
			locationName: user.location?.name || "",
			...(isUpdateProfile ? {} : { name: user.name }),
		};
	}, [user, defaultValues, hasSavedData, isUpdateProfile]);

	const {
		register,
		handleSubmit,
		setValue,
		control,
		watch,
		trigger,
		getValues,
		reset,
		formState: { errors },
	} = useForm({
		mode: "onBlur",
		defaultValues: initialValues,
	});

	// Reset form when user data loads
	useEffect(() => {
		if (hasSavedData) {
			reset(initialValues);
			if (user.location) {
				const { latitude, longitude, name } = user.location;
				setSelectedPlace({
					lat: latitude,
					lng: longitude,
					description: name
				});

				// Derive timezone from location coordinates if location exists
				// Pass date/time explicitly since watched values may not be updated yet
				if (latitude && longitude && user.date) {
					const { year, month, day } = user.date;
					const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
					const timeStr = user.time
						? `${String(user.time.hour).padStart(2, '0')}:${String(user.time.minute).padStart(2, '0')}`
						: '12:00';

					const derivedOffset = deriveTimezoneOffset(latitude, longitude, dateStr, timeStr);
					if (derivedOffset) {
						setValue("timezone", derivedOffset, { shouldValidate: true });
					}
				}
			}
		}
	}, [hasSavedData, initialValues, reset, user, setValue]);

	const locationNameValue = watch("locationName") ?? "";
	const dateValue = watch("date");
	const timeValue = watch("time");

	const roundToNearest30Min = (offsetMinutes) => {
		const sign = offsetMinutes >= 0 ? 1 : -1;
		const abs = Math.abs(offsetMinutes);
		// Round to nearest 30 minutes, but preserve exact 30-minute increments
		const rounded = Math.round(abs / 30) * 30;
		return sign * rounded;
	};

	const minutesToOffsetString = (offsetMinutes) => {
		const sign = offsetMinutes >= 0 ? "+" : "-";
		const abs = Math.abs(offsetMinutes);
		const hours = Math.floor(abs / 60);
		const minutes = abs % 60;
		return `${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(
			2,
			"0",
		)}`;
	};

	const deriveTimezoneOffset = (lat, lng, explicitDate = null, explicitTime = null) => {
		try {
			const tzName = tzLookup(lat, lng); // IANA timezone name (e.g., 'Asia/Kolkata' for Delhi)

			// Always use a valid date/time for timezone calculation
			// If user hasn't entered a date yet, use current date to get accurate offset
			let dateStr = explicitDate ?? dateValue;
			let timeStr = explicitTime ?? timeValue;

			// Ensure we have a valid date - use current date if not provided
			if (!dateStr || dateStr === "") {
				const now = moment.tz(tzName);
				dateStr = now.format("YYYY-MM-DD");
				timeStr = timeStr || now.format("HH:mm");
			} else {
				// Use provided time or default to noon if not specified
				timeStr = timeStr || "12:00";
			}

			// Create moment object in the specific timezone for the given date/time
			// This gives us the accurate UTC offset for that location at that specific date/time
			const m = moment.tz(`${dateStr} ${timeStr}`, "YYYY-MM-DD HH:mm", tzName);
			if (!m.isValid()) {
				console.warn(
					"Invalid date/time for timezone calculation, using current time",
				);
				const now = moment.tz(tzName);
				const offsetMin = now.utcOffset();
				return minutesToOffsetString(offsetMin);
			}

			const offsetMin = m.utcOffset(); // minutes from UTC (e.g., 330 for +05:30, 360 for +06:00)

			// Use the exact offset as calculated by moment-timezone
			// moment-timezone already handles historical timezone changes correctly
			// We need to match this to the nearest available option in our dropdown (which supports 30-minute increments)
			const absOffset = Math.abs(offsetMin);
			const remainder = absOffset % 30;

			// For offsets that are already on 30-minute boundaries, use them exactly
			// For offsets with non-30-minute remainder (like 15 or 45 minutes), round to nearest 30 minutes
			let finalOffset = offsetMin;
			if (remainder !== 0) {
				// Round to nearest 30-minute increment only if not already aligned
				finalOffset = roundToNearest30Min(offsetMin);
			}

			// Ensure the result matches a valid dropdown option
			const result = minutesToOffsetString(finalOffset);

			// Debug logging to help diagnose timezone issues
			if (process.env.NODE_ENV === "development") {
				console.log("Timezone derivation:", {
					tzName,
					lat,
					lng,
					dateStr,
					timeStr,
					offsetMin,
					finalOffset,
					result,
				});
			}

			return result;
		} catch (e) {
			console.error("Error deriving timezone offset:", e, { lat, lng });
			return null;
		}
	};

	useEffect(() => {
		// Only auto-detect timezone if:
		// 1. No defaultValues timezone AND
		// 2. User doesn't have saved timezone data
		if (!defaultValues?.timezone && !user?.timezone && Intl?.DateTimeFormat) {
			const offsetMinutes = new Date().getTimezoneOffset();
			const sign = offsetMinutes <= 0 ? "+" : "-";
			const absMinutes = Math.abs(offsetMinutes);
			const hours = Math.floor(absMinutes / 60);
			const minutes = absMinutes % 60;
			const formatted = `${sign}${String(hours).padStart(2, "0")}:${String(
				minutes,
			).padStart(2, "0")}`;
			setValue("timezone", formatted);
		}
	}, [defaultValues?.timezone, user?.timezone, setValue]);

	// Re-validate when selectedPlace changes to clear error message
	useEffect(() => {
		if (selectedPlace) {
			trigger("locationName");
		}
	}, [selectedPlace, trigger]);

	// Debounce input changes to avoid excessive API calls
	useEffect(() => {
		// Clear existing timer
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		// Set new timer for debouncing (300ms delay)
		debounceTimerRef.current = setTimeout(() => {
			// Debounced value is ready - Autocomplete will handle the search
		}, 300);

		// Cleanup on unmount or when value changes
		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, [locationNameValue]);

	const handlePlaceSelect = async () => {
		if (!autocompleteRef.current) return;

		const place = autocompleteRef.current.getPlace();
		if (!place || !place.geometry) return;

		const { lat, lng } = place.geometry.location;
		const description =
			place.formatted_address || place.name || locationNameValue;

		const newSelectedPlace = {
			lat: lat(),
			lng: lng(),
			description,
		};

		setSelectedPlace(newSelectedPlace);
		setValue("locationName", description, { shouldValidate: false });

		// Derive timezone locally (no external Time Zone API) and set offset
		const derivedOffset = deriveTimezoneOffset(
			newSelectedPlace.lat,
			newSelectedPlace.lng,
		);
		if (derivedOffset) {
			setValue("timezone", derivedOffset, { shouldValidate: true });
		}

		// Trigger validation after state update to clear error message
		await trigger("locationName");
	};

	const handleInputChange = (value) => {
		// Clear selected place if user modifies the input
		if (selectedPlace && value !== selectedPlace.description) {
			setSelectedPlace(null);
		}
	};

	const submitHandler = (values) => {
		// Ensure a place was selected
		if (!selectedPlace) {
			setValue("locationName", "", { shouldValidate: true });
			return;
		}
		const payload = {
			date: parseDate(values.date),
			time: parseTime(values.time),
			timezone: values.timezone || getValues("timezone"),
			location: {
				name: values.locationName,
				latitude: selectedPlace.lat, // Use selectedPlace derived state
				longitude: selectedPlace.lng,
			},
			...(isUpdateProfile ? {} : { preparingFor: values.name })
		};

		onSubmit?.(payload);
	};

	if (loadError) {
		return (
			<div className='birth-form'>
				<div className='birth-form__intro'>
					<h2>Error Loading Google Maps</h2>
					<p>Unable to load Google Maps. Please check your API key configuration.</p>
					{!googleMapsApiKey && (
						<p style={{ color: "#d64545", marginTop: "0.5rem" }}>
							API key is missing. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env
							file.
						</p>
					)}
				</div>
			</div>
		);
	}

	if (!googleMapsApiKey) {
		return (
			<div className='birth-form'>
				<div className='birth-form__intro'>
					<h2>Google Maps API Key Required</h2>
					<p style={{ color: "#d64545" }}>
						Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file to use location
						autocomplete.
					</p>
				</div>
			</div>
		);
	}

	return (
		<>
			<form className='birth-form' id="birth-data-form" onSubmit={handleSubmit(submitHandler)}>
				<div className='birth-form__intro flex justify-between items-start'>
					<div>
						<h2>Birth Data</h2>
						<p>
							Enter the birth details to generate a heliocentric chart. All fields are
							required. Start typing a city name to see address suggestions.
						</p>
					</div>
				</div>

				<div className='birth-form__row'>
					{!isUpdateProfile && <div className='birth-form__field'>
						<label>Name</label>
						<input
							type='Text'
							placeholder='John Doe'
							{...register("name", { required: "Name is required" })}
						/>
						<ValidationMessage>{errors.date?.message}</ValidationMessage>
					</div>}
					<div className='birth-form__field'>
						<label>Date</label>
						<Controller
							name="date"
							control={control}
							rules={{
								required: "Date is required",
								validate: (value) =>
									!value || value >= MIN_BIRTH_DATE || "Date must be on or after 1501-01-01",
							}}
							render={({ field }) => (
								<FormattedDateInput
									name="date"
									value={field.value}
									onChange={field.onChange}
									min={MIN_BIRTH_DATE}
									placeholder="DD/MM/YYYY or YYYY-MM-DD"
								/>
							)}
						/>
						<ValidationMessage>{errors.date?.message}</ValidationMessage>
					</div>

					<div className='birth-form__field'>
						<label>Time</label>
						<input
							type='time'
							placeholder='HH:MM'
							onFocus={(e) => handleInteraction(e, 'time')}
							{...register("time", { required: "Time is required" })}
						/>
						<ValidationMessage>{errors.time?.message}</ValidationMessage>
					</div>
				</div>

				<div className='birth-form__field'>
					<label>Timezone Offset</label>
					<select
						onFocus={(e) => handleInteraction(e, 'timezone')}
						{...register("timezone", { required: "Timezone is required" })}
					>
						{timezoneOptions.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
					<ValidationMessage>{errors.timezone?.message}</ValidationMessage>
				</div>

				<div className='birth-form__field birth-form__field--autocomplete'>
					<label>
						Location Name
						{!isLoaded ? (
							<input type='text' placeholder='Loading Google Maps...' disabled />
						) : (
							<Controller
								name='locationName'
								control={control}
								rules={{
									required: "Location is required. Please select from suggestions.",
									validate: () =>
										selectedPlace !== null ||
										"Please select a location from the suggestions.",
								}}
								render={({ field }) => (
									<Autocomplete
										onLoad={(autocomplete) => {
											autocompleteRef.current = autocomplete;
											if (autocomplete.setFields) {
												autocomplete.setFields(["formatted_address", "geometry", "name"]);
											}
										}}
										onPlaceChanged={handlePlaceSelect}
										options={{
											types: ["(cities)"],
											fields: ["formatted_address", "geometry", "name"],
										}}
									>
										<input
											{...field}
											type='text'
											placeholder='Type city name...'
											value={field.value ?? ""}
											onChange={(e) => {
												if (checkRestriction('location')) return;
												const value = e.target.value || "";
												handleInputChange(value);
												field.onChange(value);
											}}
											onFocus={(e) => handleInteraction(e, 'location')}
										/>
									</Autocomplete>
								)}
							/>
						)}
					</label>
					<ValidationMessage>{errors.locationName?.message}</ValidationMessage>
					{!isLoaded && (
						<p
							className='birth-form__hint'
							style={{ fontSize: "0.8rem", color: "#51607c", marginTop: "0.25rem" }}
						>
							Loading Google Maps...
						</p>
					)}
				</div>

				<div className='flex justify-around gap-4'>
					<button
						type='submit'
						disabled={submitting || !selectedPlace}
						className={`birth-form__submit ${submitting ? "loading" : ""}`}
					>
						{submitting ? isUpdateProfile ? "Updating…" : "Calculating…" : isUpdateProfile ? "Save Changes" : "Generate Chart"}
					</button>
				</div>
			</form>

			<ReportModal
				isOpen={isReportModalOpen}
				onClose={() => setIsReportModalOpen(false)}
				onPurchase={handleReportPurchase}
			/>

			{/* Upgrade Modal */}
			{showUpgradeModal && (
				<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
					<div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative animate-in zoom-in-95 duration-200">
						<button
							onClick={() => setShowUpgradeModal(false)}
							className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
						>
							<X size={24} />
						</button>
						<div className="flex flex-col items-center text-center">
							<div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
								<AlertCircle className="text-purple-600" size={24} />
							</div>
							<h3 className="text-xl font-bold text-gray-900 mb-2">Upgrade to Edit</h3>
							<p className="text-gray-500 mb-6">
								Editing Time, Location, and Timezone is a Premium Feature.
							</p>
							<div className="flex gap-3 w-full">
								<button
									onClick={() => setShowUpgradeModal(false)}
									className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 text-sm"
								>
									Cancel
								</button>
								<button
									onClick={() => navigate('/plans')}
									className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:shadow-lg text-sm"
								>
									View Plans
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

BirthDataForm.propTypes = {
	defaultValues: PropTypes.shape({
		date: PropTypes.string,
		time: PropTypes.string,
		timezone: PropTypes.string,
		locationName: PropTypes.string,
	}),
	onSubmit: PropTypes.func,
	submitting: PropTypes.bool,
};

BirthDataForm.defaultProps = {
	defaultValues: DEFAULTS,
	onSubmit: null,
	submitting: false,
};

export default BirthDataForm;
